import React, {useEffect, useState} from "react";
import {dialog, isElectronApp, registerEvent} from "../../electron-modules";
import {
  List} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SimpleDialog } from "../../components";
import {useAuth, useDialog, useGlobalSettings} from "../../hooks";
import {
  engineCloudURL as globalEngineCloudURL,
  setEngineCloudURL as setGlobalEngineCloudURL
} from "../../urls";
import {
  AerialBackground,
  AerialTypeDropdown,
  EngineCloudURL,
  MouseControlType,
  OrthomosaicOpacity,
  OrthomosaicQuality,
  QuickNavigationToggle,
  SectionTitle,
  ViewerBackground,
  VolumeCalculation,
  VolumeSampling
} from "./components";
import { ControlType } from "../../types/controls";
import { AerialMeasureType, SamplingRate, VolumeType } from "../../types/measurements";

export const GlobalSettings = () => {
  const {signOut} = useAuth();
  const {t} = useTranslation();

  const settingsDialog = useDialog();
  const globalSettings = useGlobalSettings();

  const [openQuickNav, setOpenQuickNav] = useState(false);
  const [orthoQuality, setOrthoQuality] = useState<number>(null);
  const [orthoOpacity, setOrthoOpacity] = useState<number>(null);
  const [controlType, setControlType] = useState<ControlType>(null);
  const [aerialMeasureType, setAerialMeasureType] = useState<AerialMeasureType>(null);
  const [volumeType, setVolumeType] = useState<VolumeType>(null);
  const [volumeSampleRate, setVolumeSampleRate] = useState<SamplingRate>(null);
  const [viewerBackground, setViewerBackground] = useState("#00000");
  const [aerialBackground, setAerialBackground] = useState("#00000");
  const [engineCloudURL, setEngineCloudURL] = useState("");

  const onSubmit = () => {
    globalSettings.update({
      orthoQuality,
      orthoOpacity,
      controlType,
      openQuickNav,
      aerialMeasureType,
      volumeType,
      volumeSampleRate,
      viewerBackground,
      aerialBackground
    });

    if (isElectronApp) {
      const modified = setGlobalEngineCloudURL(engineCloudURL, true);

      if (modified) {
        signOut();
      }
    }

    settingsDialog.handleClose();
  };

  const onClose = async () => {
    if (!modified) {
      settingsDialog.handleClose();
      return;
    }

    const result = await dialog.showMessageBox({
      type: 'question',
      title: t('global-settings.discard_changes'),
      message: t('global-settings.any_unsaved_changes_will_be_lost'),
      buttons: [
        t("buttons.cancel"),
        t('buttons.discard_changes')
      ],
      noLink: true
    });

    if (!result.response) return;
    settingsDialog.handleClose();
  };

  useEffect(() => {
    if (!settingsDialog.open) {
      return;
    }

    // Load current state from global settings context
    setOrthoQuality(globalSettings.orthoQuality);
    setOrthoOpacity(globalSettings.orthoOpacity);
    setControlType(globalSettings.controlType);
    setAerialMeasureType(globalSettings.aerialMeasureType);
    setVolumeType(globalSettings.volumeType);
    setVolumeSampleRate(globalSettings.volumeSampleRate);
    setViewerBackground(globalSettings.viewerBackground);
    setAerialBackground(globalSettings.aerialBackground);
    setOpenQuickNav(globalSettings.openQuickNav);

    if (isElectronApp) {
      setEngineCloudURL(globalEngineCloudURL);
    }
  }, [settingsDialog.open]);

  useEffect(() => {
    registerEvent("open-global-settings", () => {
      settingsDialog.handleOpen();
    });
  }, []);

  const modified = (engineCloudURL !== globalEngineCloudURL)
    || (orthoQuality !== globalSettings.orthoQuality)
    || (orthoOpacity !== globalSettings.orthoOpacity)
    || (controlType !== globalSettings.controlType)
    || (openQuickNav !== globalSettings.openQuickNav)
    || (aerialMeasureType !== globalSettings.aerialMeasureType)
    || (volumeType !== globalSettings.volumeType)
    || (volumeSampleRate !== globalSettings.volumeSampleRate)
    || (viewerBackground !== globalSettings.viewerBackground)
    || (aerialBackground !== globalSettings.aerialBackground);

  return (
    <SimpleDialog
      open={settingsDialog.open}
      onClose={onClose}
      onSubmit={onSubmit}
      title={t("global-settings.viewer-settings")}
      button={t('buttons.save-changes')}
      canSubmit={modified}
    >
      {/* Global Settings */}
      <List dense style={{paddingTop: 0}}>
        {/** General settings */}
        <SectionTitle text={t('global-settings.general_settings')}/>
        <ViewerBackground value={viewerBackground} setValue={setViewerBackground}/>
        <AerialBackground value={aerialBackground} setValue={setAerialBackground}/>
        <MouseControlType value={controlType} setValue={setControlType}/>
        <QuickNavigationToggle value={openQuickNav} setValue={setOpenQuickNav}/>

        {/** Measurement settings */}
        <SectionTitle text={t('global-settings.measurement_settings')}/>
        <AerialTypeDropdown value={aerialMeasureType} setValue={setAerialMeasureType}/>
        <VolumeCalculation value={volumeType} setValue={setVolumeType}/>
        <VolumeSampling value={volumeSampleRate} setValue={setVolumeSampleRate}/>

        {/** Orthomosaic settings */}
        <SectionTitle text={t("global-settings.orthomosaic-settings")}/>
        <OrthomosaicOpacity value={orthoOpacity} setValue={setOrthoOpacity}/>
        <OrthomosaicQuality value={orthoQuality} setValue={setOrthoQuality}/>

        {/** Engine cloud settings */}
        <SectionTitle text={t("global-settings.engine_cloud_settings")}/>
        <EngineCloudURL value={engineCloudURL} setValue={setEngineCloudURL}/>
      </List>
    </SimpleDialog>
  );
};
